import { createAsyncThunk } from '@reduxjs/toolkit';
import QueryString from 'qs';
import store from '../..';
import ApiaApi, { makeApiaUrl } from '../../../api/apiaApi/apiaApi';
import { getCurrentListId } from '../../../components/apia/common/lists/hooks/useCurrentListId';
import { TAutocompleteItem } from '../../../components/common/generic/AutocompleteInput/Index';
import { TApiaFilterValue, TApiaFunction, TApiaLoad } from '../../../types';
import { hasSucceedFormValidation } from '../../validation/util';
import validationsStore from '../../validation/validationStore';

const setFilters = createAsyncThunk(
  'DocumentsMonitorSlice/setFilters',
  async () => {
    const id = getCurrentListId();

    const varsFilters = store.getState().listSlice.lists[id].filters;
    const metaFilters = store.getState().documentsMonitorSlice.metaFilters[id];
    const { freeMetaFilters } =
      store.getState().documentsMonitorSlice.metaFilters[id];
    const { documentsOrigin } =
      store.getState().documentsMonitorSlice.metaFilters[id];
    const postData: Record<string, TApiaFilterValue> = {};

    if (freeMetaFilters.length > 0) {
      const validationFilters =
        await validationsStore.validateForm<
          Record<string, TApiaFilterValue | TAutocompleteItem>
        >('MetadataFilters');

      if (
        hasSucceedFormValidation(validationFilters) &&
        Object.values(validationFilters.submitValues).some(
          (value) => value !== '',
        )
      ) {
        varsFilters?.forEach((filter) => {
          if (!filter.isRange) {
            if (['date', 'D'].includes(filter.type as string)) {
              if (filter.currentValue) {
                const valueFrom =
                  typeof filter.currentValue !== 'string' && filter.currentValue
                    ? filter.currentValue.toString()
                    : filter.currentValue;

                postData[filter.id] = valueFrom;
              }
              if (filter.filterToValue && filter.filterToId) {
                const valueTo =
                  typeof filter.filterToValue !== 'string' &&
                  filter.filterToValue
                    ? filter.filterToValue.toString()
                    : filter.filterToValue;

                postData[filter.filterToId] = valueTo;
              }
            } else {
              const value =
                typeof filter.currentValue !== 'string' && filter.currentValue
                  ? filter.currentValue.toString()
                  : filter.currentValue;
              postData[filter.id] = value ?? '';
            }
          } else if (filter.isRange) {
            if (filter.currentValue) {
              const valueFrom =
                typeof filter.currentValue !== 'string' && filter.currentValue
                  ? filter.currentValue.toString()
                  : filter.currentValue;

              postData[filter.id] = valueFrom;
            }
            if (filter.filterToValue && filter.filterToId) {
              const valueTo =
                typeof filter.filterToValue !== 'string' && filter.filterToValue
                  ? filter.filterToValue.toString()
                  : filter.filterToValue;

              postData[filter.filterToId] = valueTo;
            }
          }
        });

        const valuesMap: Record<string, { name: string; value: string }> = {};

        Object.entries(validationFilters.values).forEach(([name, value]) => {
          const key = name.match(/_(\d+)$/)?.[1] as string;
          if (validationFilters.values[`name_${key}`]) {
            if (!valuesMap[key])
              valuesMap[key] = {} as { name: string; value: string };

            if (typeof value === 'string' || typeof value === 'number') {
              valuesMap[key].value = String(value);
            } else if (typeof value === 'object')
              valuesMap[key].name = value.id;
          }
        });
        const strDocFreeMetadataArray: string[] = [];
        Object.entries(valuesMap)
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          .forEach(([_, current]) => {
            if (current.name && current.value) {
              strDocFreeMetadataArray.push(
                `${current.name}${window.PRIMARY_SEPARATOR}${current.value}`,
              );
            }
          });
        const strDocFreeMetadata = strDocFreeMetadataArray.join(';');

        postData.strDocFreeMetadata = strDocFreeMetadata;
        const values: string[] = [];
        metaFilters.filters.forEach((filter) => {
          if (filter.currentValue !== '') {
            if (filter.type) {
              values.push(
                `${filter.id}${window.PRIMARY_SEPARATOR}${filter.type}${window.PRIMARY_SEPARATOR}${filter.currentValue}`,
              );
            } else {
              values.push(
                `${filter.id}${window.PRIMARY_SEPARATOR}${filter.currentValue};`,
              );
            }
          }
        });
        const strDocMetadata = values.join(';');

        postData.strDocMetadata = strDocMetadata;
      }
    } else {
      varsFilters?.forEach((filter) => {
        if (!filter.isRange) {
          if (['date', 'D'].includes(filter.type as string)) {
            if (filter.currentValue) {
              const valueFrom =
                typeof filter.currentValue !== 'string' && filter.currentValue
                  ? filter.currentValue.toString()
                  : filter.currentValue;

              postData[filter.id] = valueFrom;
            }
            if (filter.filterToValue && filter.filterToId) {
              const valueTo =
                typeof filter.filterToValue !== 'string' && filter.filterToValue
                  ? filter.filterToValue.toString()
                  : filter.filterToValue;

              postData[filter.filterToId] = valueTo;
            }
          } else {
            const value =
              typeof filter.currentValue !== 'string' && filter.currentValue
                ? filter.currentValue.toString()
                : filter.currentValue;
            postData[filter.id] = value ?? '';
          }
        } else if (filter.isRange) {
          if (filter.currentValue) {
            const valueFrom =
              typeof filter.currentValue !== 'string' && filter.currentValue
                ? filter.currentValue.toString()
                : filter.currentValue;

            postData[filter.id] = valueFrom;
          }
          if (filter.filterToValue && filter.filterToId) {
            const valueTo =
              typeof filter.filterToValue !== 'string' && filter.filterToValue
                ? filter.filterToValue.toString()
                : filter.filterToValue;

            postData[filter.filterToId] = valueTo;
          }
        }
      });
      const values: string[] = [];
      metaFilters.filters.forEach((filter) => {
        if (filter.currentValue !== '') {
          if (filter.type) {
            values.push(
              `${filter.id}${window.PRIMARY_SEPARATOR}${filter.type}${window.PRIMARY_SEPARATOR}${filter.currentValue}`,
            );
          } else {
            values.push(
              `${filter.id}${window.PRIMARY_SEPARATOR}${filter.currentValue};`,
            );
          }
        }
      });
      const strDocMetadata = values.join(';');

      postData.strDocMetadata = strDocMetadata;
    }

    if (documentsOrigin) {
      postData.selType = documentsOrigin;
    }

    if (metaFilters.docType) {
      postData.txtDocTypeId = metaFilters.docType;
    }

    await ApiaApi.post<TApiaLoad<TApiaFunction>>(
      makeApiaUrl({
        action: 'setFilters',
        isAjax: true,
        preventAsXmlParameter: true,
      }),
      {
        postData: QueryString.stringify(postData),
      },
    );

    return postData;
  },
);

export default setFilters;
